import React from 'react';
import { ArrowRight, CreditCard, TreePine, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CardPage = () => {
  const navigate = useNavigate();
  
  const handleNavigate = (url) => {
    navigate(url);
  };

  const cards = [
    {
      id: 1,
      title: "Carte OCI",
      description: "Obtenez votre carte OCI rapidement et facilement avec nos services dédiés",
      icon: <CreditCard className="w-6 h-6 sm:w-8 sm:h-8" />,
      gradient: "from-orange-500 to-orange-600",
      hoverGradient: "from-orange-600 to-orange-700",
      url: "/ocisms"
    },
    {
      id: 2,
      title: "Recherche Ancêtre Indien",
      description: "Explorez vos racines indiennes avec notre service de recherche d'ancêtres",
      icon: <UserCheck className="w-6 h-6 sm:w-8 sm:h-8" />,
      gradient: "from-white to-gray-100",
      hoverGradient: "from-gray-100 to-gray-200",
      url: "/ancetresms"
    },
    {
      id: 3,
      title: "Arbre Généalogique",
      description: "Créez votre arbre généalogique avec notre formulaire intuitif",
      icon: <TreePine className="w-6 h-6 sm:w-8 sm:h-8" />,
      gradient: "from-green-600 to-green-700",
      hoverGradient: "from-green-700 to-green-800",
      url: "/arbresms"
    }
  ];

  const handleCardClick = (url) => {
    handleNavigate(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50 dark:from-slate-900 dark:to-slate-900 p-4 sm:p-6 lg:p-8 transition-colors duration-300">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent">
            Découvrez Nos Services
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-4">
            Choisissez la prestation qui vous intéresse.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {cards.map((card, index) => (
            <div
              key={card.id}
              className="group relative cursor-pointer transform transition-all duration-500 hover:scale-105"
              onClick={() => handleCardClick(card.url)}
              style={{
                animationDelay: `${index * 0.2}s`
              }}
            >
              {/* Card Background with Glassmorphism */}
              <div className="relative h-72 sm:h-80 lg:h-96 rounded-2xl backdrop-blur-lg bg-white/60 dark:bg-white/10 border border-gray-200 dark:border-white/20 shadow-2xl dark:shadow-green-900/20 overflow-hidden">
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                
                {/* Animated Background Particles */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute -top-2 -right-2 w-16 h-16 sm:w-20 sm:h-20 bg-gray-300/30 dark:bg-white/10 rounded-full blur-xl animate-pulse" />
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 sm:w-32 sm:h-32 bg-gray-300/20 dark:bg-white/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}} />
                </div>

                {/* Content */}
                <div className="relative z-10 p-4 sm:p-6 lg:p-8 h-full flex flex-col justify-between">
                  {/* Icon */}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center ${card.gradient.includes('white') ? 'text-gray-800' : 'text-white'} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    {card.icon}
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 flex flex-col justify-center mt-4 sm:mt-6">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4 group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors duration-300 leading-tight">
                      {card.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-4 sm:mb-6">
                      {card.description}
                    </p>
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-white transition-colors duration-300">
                      Cliquez pour explorer
                    </span>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-800/20 dark:bg-white/20 flex items-center justify-center group-hover:bg-gray-800/30 dark:group-hover:bg-white/30 transition-all duration-300 group-hover:translate-x-1">
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800 dark:text-white" />
                    </div>
                  </div>
                </div>

                {/* Hover Effect Border */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${card.hoverGradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none`} />
              </div>

              {/* Glow Effect */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10`} />
            </div>
          ))}
        </div>

        {/* Floating Elements - Hidden on mobile for better performance */}
        <div className="hidden sm:block fixed top-20 left-10 w-2 h-2 bg-orange-400 rounded-full animate-ping opacity-60" />
        <div className="hidden sm:block fixed top-40 right-20 w-3 h-3 bg-green-400 rounded-full animate-pulse opacity-40" />
        <div className="hidden sm:block fixed bottom-20 left-1/4 w-1 h-1 bg-orange-400 rounded-full animate-bounce opacity-50" />
      </div>
    </div>
  );
};

export default CardPage;